import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import { BookOpenIcon, FlaskConicalIcon, PencilIcon, LandmarkIcon, GlobeIcon, PaletteIcon, MusicIcon, CodeIcon, ActivityIcon, DnaIcon, CalculatorIcon, BeakerIcon, BoxIcon, EditIcon, EyeIcon, ArchiveIcon, MoreVerticalIcon, Share2Icon, CopyIcon, TrashIcon, XCircleIcon, TagIcon, UserPlusIcon, UsersIcon, PlayIcon, ChevronDownIcon, LinkIcon, CheckCircleIcon, SettingsIcon, RotateCcwIcon, ExternalLinkIcon, PlusIcon, ListIcon, FileTextIcon } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { StatusBadge, AssessmentStatus } from './StatusBadge';
import { StatusBadge as AssessmentStatusBadge } from './AssessmentStatusBadge';
import { AssessmentActionsMenu } from './AssessmentActionsMenu';
import type { StatusType } from './AssessmentStatusBadge';
interface TableViewProps {
  assessments: any[];
  onPreview: (assessment: any) => void;
  onShareWithTeachers?: (assessment: any) => void;
  onAction?: (action: string, assessment: any) => void;
  onOpenAssignments?: (assessment: any) => void;
  onQuickShare?: (assessment: any) => void;
}
// Icon mapping function based on title and tags (same as PublicAssessmentCard)
const getIconForAssessment = (title: string, tags: string[]): any => {
  const lowerTitle = title.toLowerCase();
  const lowerTags = tags.map(tag => tag.toLowerCase()).join(' ');
  const combined = `${lowerTitle} ${lowerTags}`;
  if (combined.includes('math') || combined.includes('algebra') || combined.includes('fraction') || combined.includes('calculation')) {
    return CalculatorIcon;
  }
  if (combined.includes('science') || combined.includes('physics') || combined.includes('chemistry') || combined.includes('matter')) {
    return BeakerIcon;
  }
  if (combined.includes('biology') || combined.includes('body') || combined.includes('human') || combined.includes('dna')) {
    return DnaIcon;
  }
  if (combined.includes('english') || combined.includes('grammar') || combined.includes('writing') || combined.includes('literature')) {
    return PencilIcon;
  }
  if (combined.includes('history') || combined.includes('civilization') || combined.includes('ancient')) {
    return LandmarkIcon;
  }
  if (combined.includes('geography') || combined.includes('continent') || combined.includes('ocean') || combined.includes('map')) {
    return GlobeIcon;
  }
  if (combined.includes('art') || combined.includes('painting') || combined.includes('renaissance')) {
    return PaletteIcon;
  }
  if (combined.includes('music') || combined.includes('theory') || combined.includes('note')) {
    return MusicIcon;
  }
  if (combined.includes('computer') || combined.includes('programming') || combined.includes('algorithm') || combined.includes('code')) {
    return CodeIcon;
  }
  if (combined.includes('physical') || combined.includes('sport') || combined.includes('pe') || combined.includes('team')) {
    return ActivityIcon;
  }
  return BookOpenIcon;
};
const getIconColor = (title: string, tags: string[]): string => {
  const lowerTitle = title.toLowerCase();
  const lowerTags = tags.map(tag => tag.toLowerCase()).join(' ');
  const combined = `${lowerTitle} ${lowerTags}`;
  if (combined.includes('math') || combined.includes('algebra')) return '#2563EB';
  if (combined.includes('science') || combined.includes('chemistry')) return '#00B75F';
  if (combined.includes('biology') || combined.includes('dna')) return '#8B5CF6';
  if (combined.includes('english') || combined.includes('grammar')) return '#FF9D33';
  if (combined.includes('history')) return '#FF6CA1';
  if (combined.includes('geography')) return '#2563EB';
  if (combined.includes('art')) return '#FF6CA1';
  if (combined.includes('music')) return '#8B5CF6';
  if (combined.includes('computer') || combined.includes('programming')) return '#2563EB';
  if (combined.includes('physical') || combined.includes('sport')) return '#00B75F';
  return '#00B75F';
};
const getTagColor = (index: number) => {
  const colors = ['bg-[#2563EB] bg-opacity-10 text-[#2563EB]', 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]', 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]', 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1]', 'bg-[#8B5CF6] bg-opacity-10 text-[#8B5CF6]'];
  return colors[index % colors.length];
};
// Map teacher-side assessment status → draft | ready | archive
const getAssessmentStatusType = (status: string): StatusType => {
  switch (status) {
    case 'Ready to share':
    case 'Active':
    case 'Scheduled':
      return 'ready';
    case 'Archived':
    case 'Access closed':
      return 'archive';
    case 'Not yet published':
    default:
      return 'draft';
  }
};
// Minimal icon button with tooltip
const IconAction = ({
  icon: Icon,
  label,
  onClick,
  color = 'default',
  badge
}: {
  icon: any;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  color?: 'green' | 'blue' | 'default';
  badge?: number;
}) => {
  const colorClass = {
    green: 'text-[#00B75F] hover:bg-[#00B75F]/10',
    blue: 'text-[#2563EB] hover:bg-[#2563EB]/10',
    default: 'text-text-tertiary hover:text-text-primary hover:bg-surface'
  }[color];
  return <Tooltip.Provider delayDuration={300} data-id="element-2406">
      <Tooltip.Root data-id="element-2407">
        <Tooltip.Trigger asChild data-id="element-2408">
          <button onClick={onClick} className={`relative flex items-center justify-center w-7 h-7 rounded-lg transition-all ${colorClass}`} aria-label={label} data-id="element-2409">
            <Icon size={15} data-id="element-2410" />
            {badge !== undefined && badge > 0 && <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none ${color === 'green' ? 'bg-[#00B75F]' : color === 'blue' ? 'bg-[#2563EB]' : 'bg-[#2563EB]'}`} data-id="element-2411">
                {badge}
              </span>}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal data-id="element-2412">
          <Tooltip.Content className="bg-gray-900 text-white text-xs px-2 py-1 rounded-md shadow-lg z-[200]" sideOffset={5} data-id="element-2413">
            {label}
            <Tooltip.Arrow className="fill-gray-900" data-id="element-2414" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>;
};
const TableRow = ({
  assessment,
  index,
  onPreview,
  onShareWithTeachers,
  onAction,
  onOpenAssignments,
  onQuickShare
}) => {
  const IconComponent = getIconForAssessment(assessment.title, assessment.tags || []);
  const iconColor = getIconColor(assessment.title, assessment.tags || []);
  const isDraft = assessment.status === 'Not yet published';
  const isArchived = assessment.status === 'Archived';
  const isReady = assessment.status === 'Ready to share';
  const activeAssignments = assessment.assignments?.filter(a => a.status === 'Active') || [];
  const activeAssignmentsCount = activeAssignments.length;
  const hasActiveAssignments = activeAssignmentsCount > 0;
  const hasAttempts = (assessment.attempts || 0) > 0;
  const handleRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('[data-radix-collection-item]')) {
      return;
    }
    onPreview(assessment);
  };
  // Map assessment status to AssessmentActionsMenu status
  const menuStatus: 'Ready' | 'Archived' | 'Draft' = isReady ? 'Ready' : isArchived ? 'Archived' : 'Draft';
  const renderActionPanel = () => {
    // 1. Draft State
    if (isDraft) {
      return <div className="flex items-center gap-1 justify-end" data-id="element-2415">
          <IconAction icon={EditIcon} label="Edit Assessment" color="green" onClick={e => {
          e.stopPropagation();
          onAction && onAction('Edit', assessment);
        }} data-id="element-2416" />
          <AssessmentActionsMenu status="Draft" onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2417" />
        </div>;
    }
    // 2. Archived State
    if (isArchived) {
      return <div className="flex items-center gap-1 justify-end" data-id="element-2418">
          <IconAction icon={RotateCcwIcon} label="Restore to Ready" color="green" onClick={e => {
          e.stopPropagation();
          onAction && onAction('Restore', assessment);
        }} data-id="element-2419" />
          <AssessmentActionsMenu status="Archived" onReopenWorksheet={() => onAction && onAction('Restore', assessment)} onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2420" />
        </div>;
    }
    // 3. Ready State
    if (isReady) {
      return <div className="flex items-center gap-1 justify-end" data-id="element-2421">
          {hasActiveAssignments ? <IconAction icon={UsersIcon} label={`View Assignments (${activeAssignmentsCount} active)`} color="green" badge={activeAssignmentsCount} onClick={e => {
          e.stopPropagation();
          onOpenAssignments && onOpenAssignments(assessment);
        }} data-id="element-2422" /> : <IconAction icon={Share2Icon} label="Quick Share" color="green" onClick={e => {
          e.stopPropagation();
          onQuickShare && onQuickShare(assessment);
        }} data-id="element-2423" />}
          <AssessmentActionsMenu status="Ready" hasAttempts={hasAttempts} onCloseWorksheet={() => onAction && onAction('Archive', assessment)} onRevertToDraft={() => onAction && onAction('Revert to Draft', assessment)} onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2424" />
        </div>;
    }
    return null;
  };
  return <motion.tr initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} className="hover:bg-surface transition-colors group" data-id="element-2425">
      <td className="px-6 py-4" data-id="element-2426">
        <div className="flex items-center gap-3" data-id="element-2427">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 relative" style={{
          backgroundColor: `${iconColor}15`
        }} data-id="element-2428">
            <IconComponent size={24} style={{
            color: iconColor
          }} data-id="element-2429" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-2430">
            <div className="flex items-center gap-2 mb-1.5" data-id="element-2431">
              <button onClick={() => onPreview(assessment)} className="font-semibold text-text-primary hover:text-[#00B75F] transition-colors text-left cursor-pointer" data-id="element-2432">
                {assessment.title}
              </button>
              {assessment.version != null && <span className="text-[11px] font-normal text-text-tertiary cursor-default select-none" data-id="element-2433">
                  v{assessment.version}
                </span>}
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-2434">
              {assessment.tags && assessment.tags.length > 0 ? assessment.tags.map((tag, idx) => <span key={idx} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getTagColor(idx)}`} data-id="element-2435">
                    {tag}
                  </span>) : <span className="text-xs text-text-tertiary italic" data-id="element-2436">
                  No tags
                </span>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4" data-id="element-2437">
        <AssessmentStatusBadge status={getAssessmentStatusType(assessment.status)} size="md" data-id="element-2438" />
      </td>
      <td className="px-6 py-4 text-sm font-semibold text-text-primary" data-id="element-2439">
        {assessment.questions}
      </td>
      <td className="px-6 py-4" data-id="element-2440">{renderActionPanel()}</td>
    </motion.tr>;
};
// Mobile Card Component (Simplified for brevity, but should match logic)
const MobileAssessmentCard = ({
  assessment,
  index,
  onPreview,
  onShareWithTeachers,
  onAction,
  onOpenAssignments,
  onQuickShare
}) => {
  const IconComponent = getIconForAssessment(assessment.title, assessment.tags || []);
  const iconColor = getIconColor(assessment.title, assessment.tags || []);
  const isDraft = assessment.status === 'Not yet published';
  const isArchived = assessment.status === 'Archived';
  const isReady = assessment.status === 'Ready to share';
  const activeAssignmentsCount = assessment.assignments?.filter((a: any) => a.status === 'Active').length ?? 0;
  const hasActiveAssignments = activeAssignmentsCount > 0;
  const hasAttempts = (assessment.attempts || 0) > 0;
  return <div className="px-4 py-3 border-b border-custom hover:bg-surface transition-colors" onClick={() => onPreview(assessment)} data-id="element-2441">
      <div className="flex items-center gap-3" data-id="element-2442">
        {/* Icon */}
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{
        backgroundColor: `${iconColor}15`
      }} data-id="element-2443">
          <IconComponent size={18} style={{
          color: iconColor
        }} data-id="element-2444" />
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0" data-id="element-2445">
          <div className="flex items-center gap-1.5 min-w-0" data-id="element-2446">
            <button onClick={() => onPreview(assessment)} className="font-semibold text-text-primary hover:text-[#00B75F] transition-colors text-sm truncate leading-tight text-left cursor-pointer" data-id="element-2447">
              {assessment.title}
            </button>
            {assessment.version != null && <span className="text-[10px] font-normal text-text-tertiary cursor-default select-none flex-shrink-0" data-id="element-2448">
                v{assessment.version}
              </span>}
            <AssessmentStatusBadge status={getAssessmentStatusType(assessment.status)} size="sm" data-id="element-2449" />
          </div>
          <div className="flex flex-wrap gap-1 mt-0.5" data-id="element-2450">
            {assessment.tags?.slice(0, 2).map((tag: string, idx: number) => <span key={idx} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${getTagColor(idx)}`} data-id="element-2451">
                {tag}
              </span>)}
          </div>
        </div>

        {/* Actions — icon buttons only, no wrapping */}
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-2452">
          {isDraft && <IconAction icon={EditIcon} label="Edit Assessment" color="green" onClick={e => {
          e.stopPropagation();
          onAction && onAction('Edit', assessment);
        }} data-id="element-2453" />}
          {isReady && !hasActiveAssignments && <IconAction icon={Share2Icon} label="Quick Share" color="green" onClick={e => {
          e.stopPropagation();
          onQuickShare && onQuickShare(assessment);
        }} data-id="element-2454" />}
          {isReady && hasActiveAssignments && <IconAction icon={UsersIcon} label={`View Assignments (${activeAssignmentsCount} active)`} color="green" badge={activeAssignmentsCount} onClick={e => {
          e.stopPropagation();
          onOpenAssignments && onOpenAssignments(assessment);
        }} data-id="element-2455" />}
          {isArchived && <IconAction icon={RotateCcwIcon} label="Restore to Ready" color="green" onClick={e => {
          e.stopPropagation();
          onAction && onAction('Restore', assessment);
        }} data-id="element-2456" />}
          {isDraft && <AssessmentActionsMenu status="Draft" onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2457" />}
          {isArchived && <AssessmentActionsMenu status="Archived" onReopenWorksheet={() => onAction && onAction('Restore', assessment)} onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2458" />}
          {isReady && <AssessmentActionsMenu status="Ready" hasAttempts={hasAttempts} onCloseWorksheet={() => onAction && onAction('Archive', assessment)} onRevertToDraft={() => onAction && onAction('Revert to Draft', assessment)} onMakeNewVersion={() => onAction && onAction('Duplicate', assessment)} onSettings={() => onAction && onAction('Settings', assessment)} data-id="element-2459" />}
        </div>
      </div>
    </div>;
};
const TableView: React.FC<TableViewProps> = ({
  assessments,
  onPreview,
  onShareWithTeachers,
  onAction,
  onOpenAssignments,
  onQuickShare
}) => {
  return <div className="bg-surface-elevated border border-custom rounded-2xl overflow-hidden shadow-custom-md" data-id="element-2460">
      <div className="hidden md:block overflow-x-auto" data-id="element-2461">
        <table className="w-full" data-id="element-2462">
          <thead className="bg-surface border-b border-custom" data-id="element-2463">
            <tr data-id="element-2464">
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2465">
                Assessment
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2466">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2467">
                Questions
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2468">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-custom" data-id="element-2469">
            {assessments.map((assessment, index) => <TableRow key={assessment.id} assessment={assessment} index={index} onPreview={onPreview} onShareWithTeachers={onShareWithTeachers} onAction={onAction} onOpenAssignments={onOpenAssignments} onQuickShare={onQuickShare} data-id="element-2470" />)}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-custom" data-id="element-2471">
        {assessments.map((assessment, index) => <MobileAssessmentCard key={assessment.id} assessment={assessment} index={index} onPreview={onPreview} onShareWithTeachers={onShareWithTeachers} onAction={onAction} onOpenAssignments={onOpenAssignments} onQuickShare={onQuickShare} data-id="element-2472" />)}
      </div>
    </div>;
};
export default TableView;